import { ActivityStatusDetails } from '../../../shared/hooks/useActivityStatuses/useActivityStatuses.types'
import { FilterDefinition, getFilterColumnId, getUniqueFilterOptions } from '../../../shared/utils/tableFilters'
import { createFilterBuilders, createNumberBandFilter, NumberBand } from '../../../shared/utils/tableFilterFactories'
import {
    ALWAYS_VISIBLE_ACTIVITY_COLUMN_IDS,
    getActivityCategory,
    getActivityFunction,
    getActivitySource,
    getActivityTherapeuticArea,
    getBudgetLabel,
    getCompletionDateLabel,
} from './ActivityTable.constants'
import { ActivityColumnId, ActivityDisplayNames, ActivityRow } from './ActivityTable.types'
import { getDateLabel, toListLabel } from '../../../shared/utils/displayValue'

type GetStatusDetails = (status: string) => ActivityStatusDetails

const createActivityStatusFilter = (getStatusDetails: GetStatusDetails): FilterDefinition<ActivityRow> => ({
    id: 'status',
    label: 'Status',
    getOptions: (rows) =>
        getUniqueFilterOptions(
            rows.map((row) => {
                const { id, name, order } = getStatusDetails(row.status)

                return { id, label: name, order }
            })
        ),
    matches: (row, selectedOptionIds) => selectedOptionIds.includes(getStatusDetails(row.status).id),
})

const BUDGET_BANDS: NumberBand[] = [
    { id: 'upTo10k', label: 'Up to $10k', max: 10_000 },
    { id: '10kTo50k', label: '$10k - $50k', max: 50_000 },
    { id: '50kTo100k', label: '$50k - $100k', max: 100_000 },
    { id: '100kTo500k', label: '$100k - $500k', max: 500_000 },
    { id: '500kTo1m', label: '$500k - $1M', max: 1_000_000 },
    { id: 'over1m', label: 'Over $1M' },
]

export const getActivityFilterDefinitions = (
    getStatusDetails: GetStatusDetails,
    { valueMessages, strategicImperatives, tpp, evidence }: ActivityDisplayNames,
    defaultVisibleColumnIds: ReadonlySet<ActivityColumnId>
): FilterDefinition<ActivityRow>[] => {
    const { isHiddenByDefault, textFilter, multiValueFilter, yesNoFilter, linkedFilter, yearFilter, columnToggle } =
        createFilterBuilders<ActivityRow, ActivityColumnId>(defaultVisibleColumnIds)

    const definitions: FilterDefinition<ActivityRow>[] = [
        columnToggle('title', 'Short Title'),
        columnToggle('titleLong', 'Long Title'),
        textFilter('source', 'Source System', getActivitySource),
        columnToggle('externalId', 'External ID'),
        columnToggle('studyNumber', 'Study Number'),
        { ...createActivityStatusFilter(getStatusDetails), defaultHidden: isHiddenByDefault('status') },
        columnToggle('activityTimeline', 'Activity Timeline'),
        yearFilter('completionDate', 'Completion Date', (row) => row.completionDate),
        textFilter('type', 'Type', (row) => row.type),
        textFilter('category', 'Category', getActivityCategory),
        textFilter('function', 'Function', getActivityFunction),
        textFilter('ownerRoadmapName', 'Owned By', (row) => row.ownerRoadmapName ?? undefined),
        textFilter('affiliateName', 'Affiliate', (row) => row.affiliateName),
        multiValueFilter('countries', 'Countries', (row) => row.countries),
        yesNoFilter('corporateOrRnDGoal', 'Corporate / R&D Goal', (row) => !!row.corporateOrRnDGoal),
        linkedFilter('linkedValueMessageCount', `Linked ${valueMessages}`, (row) => row.linkedValueMessageCount),
        linkedFilter(
            'linkedStrategicImperativeCount',
            `Linked ${strategicImperatives}`,
            (row) => row.linkedStrategicImperativeCount
        ),
        linkedFilter('linkedTppCellCount', `Linked ${tpp} Cells`, (row) => row.linkedTppCellCount),
        linkedFilter('linkedEvidenceCount', `Linked ${evidence}`, (row) => row.linkedEvidenceCount),
        textFilter('population', 'Population', (row) => row.population),
        textFilter('sponsorType', 'Sponsor Type', (row) => row.sponsorType),
        textFilter('studyOwner', 'Study Owner', (row) => row.studyOwner),
        textFilter('providerName', 'Provider', (row) => row.providerName),
        yesNoFilter('vendor', 'Vendor', (row) => !!row.vendor),
        yesNoFilter('patientStudy', 'Patient Study', (row) => !!row.patientStudy),
        yesNoFilter('funded', 'Funded', (row) => !!row.funded),
        createNumberBandFilter<ActivityRow>(
            'budget',
            'Budget',
            (row) => row.budgetAmount,
            BUDGET_BANDS,
            isHiddenByDefault('budget')
        ),
        yearFilter('startDate', 'Start Date', (row) => row.startDateValue),
        yearFilter('endDate', 'End Date', (row) => row.endDateValue),
        yearFilter('plannedStartDate', 'Planned Start', (row) => row.plannedStartDateValue),
        yearFilter('plannedEndDate', 'Planned End', (row) => row.plannedEndDateValue),
        yearFilter('studyReportDate', 'Study Report Date', (row) => row.studyReportDateValue),
        yearFilter('resultsAvailableForExternalUseDate', 'Results Available', (row) => row.resultsAvailableDateValue),
        yearFilter('terminationDate', 'Termination Date', (row) => row.terminationDateValue),
        textFilter('therapeuticArea', 'Therapeutic Area', getActivityTherapeuticArea),
        textFilter('indication', 'Indication', (row) => row.indication),
        multiValueFilter('compoundName', 'Compound', (row) => row.compoundName),
        multiValueFilter('genericName', 'Generic Name', (row) => row.genericName),
        linkedFilter('deliverableCount', 'Deliverables', (row) => row.deliverableCount),
        linkedFilter('activityDependencyCount', 'Dependencies', (row) => row.activityDependencyCount),
        textFilter('dataSource', 'Data Source', (row) => row.dataSource),
        yearFilter('lastSyncedAt', 'Last Synced', (row) => row.lastSyncedAtValue),
        yearFilter('createdAt', 'Created', (row) => row.createdAtValue),
        yearFilter('updatedAt', 'Updated', (row) => row.updatedAtValue),
    ]

    return definitions.filter((definition) => !ALWAYS_VISIBLE_ACTIVITY_COLUMN_IDS.has(getFilterColumnId(definition)))
}

export const getActivitySearchValues = (row: ActivityRow) => ({
    title: row.title,
    titleLong: row.titleLong,
    source: getActivitySource(row),
    externalId: row.externalId,
    studyNumber: row.studyNumber,
    activityTimeline: row.activityTimelineLabel,
    completionDate: getCompletionDateLabel(row),
    type: row.type,
    category: getActivityCategory(row),
    function: getActivityFunction(row),
    ownerRoadmapName: row.ownerRoadmapName,
    affiliateName: row.affiliateName,
    countries: toListLabel(row.countries),
    population: row.population,
    sponsorType: row.sponsorType,
    studyOwner: row.studyOwner,
    providerName: row.providerName,
    budget: getBudgetLabel(row.budgetAmount),
    startDate: getDateLabel(row.startDateValue),
    endDate: getDateLabel(row.endDateValue),
    plannedStartDate: getDateLabel(row.plannedStartDateValue),
    plannedEndDate: getDateLabel(row.plannedEndDateValue),
    studyReportDate: getDateLabel(row.studyReportDateValue),
    resultsAvailableForExternalUseDate: getDateLabel(row.resultsAvailableDateValue),
    terminationDate: getDateLabel(row.terminationDateValue),
    therapeuticArea: getActivityTherapeuticArea(row),
    indication: row.indication,
    compoundName: toListLabel(row.compoundName),
    genericName: toListLabel(row.genericName),
    dataSource: row.dataSource,
    lastSyncedAt: getDateLabel(row.lastSyncedAtValue),
    createdAt: getDateLabel(row.createdAtValue),
    updatedAt: getDateLabel(row.updatedAtValue),
})
